import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, getComicById, getRecommendedComics, chaptersList } from '@/mocks/comics';
import { allCategoriesMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const comic = getComicById(Number(id));
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');
  const recommended = getRecommendedComics().filter((c) => c.id !== comic?.id).slice(0, 6);
  const relatedComics = comic
    ? comics.filter((c) => c.id !== comic.id && c.category.some((cat) => comic.category.includes(cat))).slice(0, 6)
    : [];

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的漫画不存在或已被移除</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = chaptersList[comic.id] || Array.from({ length: Math.min(comic.chapters, 10) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: '2026-08-01',
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫大全`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    image: comic.cover,
    url: window.location.href,
    publisher: {
      '@type': 'Organization',
      name: '韩漫大全',
      url: window.location.origin,
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: allCategoriesMap[comic.category[0]] || comic.category[0], href: `/category/${comic.category[0]}` },
              { label: comic.title },
            ]}
          />
        </div>

        {/* Comic Info Header */}
        <section className="py-8 md:py-10 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-[200px] h-[267px] md:w-[240px] md:h-[320px] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                    title={`${comic.title} - 韩漫大全`}
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">{comic.author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </div>
                    <span className="font-semibold text-foreground-900">{comic.rating}</span>
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-foreground-600">{comic.chapters}话</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-foreground-600">{comic.views.toLocaleString()} 次阅读</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === 'completed'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {comic.status === 'completed' ? '已完结' : '连载中'}
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6 line-clamp-4">
                  {comic.description}
                </p>

                {/* CTA */}
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/article/${comic.id}`}
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="px-8 py-3 rounded-full bg-background-100 text-foreground-700 font-semibold text-sm hover:bg-background-200/70 transition-colors whitespace-nowrap cursor-pointer">
                    加入书架
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tab Content: Info + Chapters */}
        <section className="py-8 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            {/* Tabs */}
            <div className="flex items-center gap-1 mb-6 bg-background-50 rounded-full p-1 w-fit">
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'info'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                漫画详情
              </button>
              <button
                onClick={() => setActiveTab('chapters')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'chapters'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                章节列表
              </button>
            </div>

            {activeTab === 'info' && (
              <div className="bg-background-50 rounded-lg p-6 md:p-8">
                <h2 className="text-lg font-semibold text-foreground-950 mb-4">漫画简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                  {comic.description}
                </p>

                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-background-100 text-center">
                    <p className="text-xs text-foreground-500 mb-1">总章节</p>
                    <p className="text-lg font-bold text-foreground-950">{comic.chapters}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 text-center">
                    <p className="text-xs text-foreground-500 mb-1">总阅读</p>
                    <p className="text-lg font-bold text-foreground-950">{(comic.views / 10000).toFixed(0)}万</p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 text-center">
                    <p className="text-xs text-foreground-500 mb-1">评分</p>
                    <p className="text-lg font-bold text-foreground-950">{comic.rating}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-background-100 text-center">
                    <p className="text-xs text-foreground-500 mb-1">状态</p>
                    <p className="text-lg font-bold text-foreground-950">{comic.status === 'completed' ? '已完结' : '连载中'}</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'chapters' && (
              <div className="bg-background-50 rounded-lg p-6 md:p-8">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-semibold text-foreground-950">全部章节</h2>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap">
                      正序
                    </button>
                    <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 cursor-pointer whitespace-nowrap">
                      倒序
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {chapters.map((ch) => (
                    <a
                      key={ch.num}
                      href="#"
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                    >
                      <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {ch.title}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.date}</span>
                    </a>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 text-sm hover:bg-background-200/70 transition-colors cursor-pointer">
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {[1, 2, 3, '...', 13].map((p, idx) => (
                    <button
                      key={idx}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-colors cursor-pointer ${
                        p === 1
                          ? 'bg-primary-500 text-background-50 font-semibold'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 text-sm hover:bg-background-200/70 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="medium" />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Recommended Comics */}
        {recommended.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">编辑精选</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {recommended.map((c) => (
                  <ComicCard key={c.id} comic={c} size="medium" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
    </>
  );
}